import { askClaude } from './scorecard-ai-client';
import { allFindings, bpUpdatedOn, findingsBlock } from './scorecard-best-practices-utils';
import { AuditedProduct } from './scoring-engine-constants';

/** The four rubric dimensions, each scored 0–25 → 0–100 total. */
export const ALEXA_DIMENSIONS = [
  {
    key: 'conversational',
    label: 'Conversational language',
    criteria: 'Natural, conversational phrasing with no keyword stuffing.'
  },
  {
    key: 'audience_fit',
    label: 'Audience & differentiation',
    criteria: 'States clearly who the product is for and what it does better than alternatives.'
  },
  {
    key: 'use_cases',
    label: 'Use-case coverage',
    criteria: 'Covers key use cases, occasions, or scenarios a shopper would ask about.'
  },
  {
    key: 'bullets',
    label: 'Bullet quality',
    criteria: 'Bullets complete (5 filled), each ≤150 chars, opening with a customer outcome.'
  }
] as const;

export type AlexaDimensionKey = (typeof ALEXA_DIMENSIONS)[number]['key'];

const DIMENSION_LABELS: Record<AlexaDimensionKey, string> = ALEXA_DIMENSIONS.reduce(
  (acc, d) => {
    acc[d.key] = d.label;
    return acc;
  },
  {} as Record<AlexaDimensionKey, string>
);

export interface AlexaDimensionScore {
  key: AlexaDimensionKey;
  label: string;
  score: number;
  rationale: string;
}

export interface AlexaAssessmentResult {
  score: number;
  dimensions: AlexaDimensionScore[];
  summary: string;
}

const clampScore = (val: unknown): number => {
  const n = typeof val === 'number' ? val : Number(val);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(25, Math.round(n)));
};

const isDimensionEntry = (v: unknown): v is { key: string; score?: unknown; rationale?: unknown } =>
  Boolean(v) && typeof v === 'object' && typeof (v as { key?: unknown }).key === 'string';

const normalizeDimensions = (raw: unknown): AlexaDimensionScore[] => {
  const byKey = new Map<string, { score: number; rationale: string }>();
  if (Array.isArray(raw)) {
    raw.filter(isDimensionEntry).forEach(d => {
      byKey.set(d.key, {
        score: clampScore(d.score),
        rationale: typeof d.rationale === 'string' ? d.rationale : ''
      });
    });
  }
  return ALEXA_DIMENSIONS.map(d => {
    const hit = byKey.get(d.key);
    return {
      key: d.key,
      label: DIMENSION_LABELS[d.key],
      score: hit ? hit.score : 0,
      rationale: hit ? hit.rationale : 'Not assessed.'
    };
  });
};

const buildAssessSystemPrompt = (): string => {
  const alexaFindings = allFindings.filter(
    f => f.applies_to === 'alexa_readiness' || f.applies_to === 'both'
  );
  const rubricBlock = ALEXA_DIMENSIONS.map(d => `- "${d.key}" (0–25): ${d.criteria}`).join('\n');

  return `You are an Amazon listing analyst who evaluates how well a product detail page is optimized for AI shopping assistants like Amazon's Alexa for Shopping.

You are SCORING ONLY. Do not rewrite, suggest, or rephrase any copy. Judge the listing exactly as written.

CURRENT BEST PRACTICES (governance source — updated ${bpUpdatedOn}):
${findingsBlock(alexaFindings)}

SCORING RUBRIC — score each dimension 0–25, then sum to a 0–100 total:
${rubricBlock}

Be calibrated and critical: reserve 22–25 for genuinely exemplary copy, use the mid-range for partial coverage, and score low when a dimension is absent. The "summary" must be one short sentence naming the single biggest readiness gap.

Return ONLY a JSON object (no markdown, no code fences) of the exact shape:
{"score": number, "summary": string, "dimensions": [{"key": string, "score": number, "rationale": string}]}`;
};

const buildAssessUserPrompt = (product: AuditedProduct): string => {
  const bulletsPreview =
    product.featureBullets.length > 0
      ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
      : '(none)';
  const description =
    product.fullDescription && product.fullDescription !== ' — '
      ? product.fullDescription
      : '(none)';

  return `Evaluate this listing's Alexa for Shopping readiness.

Brand: ${product.brand || '(unknown)'}
Category: ${product.category || '(unknown)'}

Title (${product.title === ' — ' ? 0 : product.title.length} chars):
${product.title === ' — ' ? '(none)' : product.title}

Bullets (${product.featureBullets.length} parsed):
${bulletsPreview}

Description:
${description}

Return the rubric scores, a one-line rationale per dimension, and a one-line summary of the biggest gap.`;
};

export const assessAlexaReadiness = async (
  product: AuditedProduct
): Promise<AlexaAssessmentResult> => {
  const raw = await askClaude({
    system: buildAssessSystemPrompt(),
    prompt: buildAssessUserPrompt(product),
    maxTokens: 2048
  });
  const r = (raw ?? {}) as Record<string, unknown>;
  const dimensions = normalizeDimensions(r.dimensions);
  // Trust the breakdown over the model's self-reported total so the headline score
  // and its rationale can never disagree in the UI.
  const score = dimensions.reduce((acc, d) => acc + d.score, 0);
  return { score, dimensions, summary: typeof r.summary === 'string' ? r.summary : '' };
};
